import type { Card, CardOpening, HandResults, LegalActions, Phase } from '@tctp/engine';

export type { Card, CardOpening, HandResults, LegalActions, Phase };

/**
 * Socket.IO event names. `serverError` avoids Socket.IO's reserved `error` event.
 * `intent` may carry a Socket.IO acknowledgement callback; the server calls it with `null` on success
 * or a `ServerError` on rejection, after the resulting `snapshot` (if any) has been sent.
 */
/**
 * Server → client: `snapshot` after every change (no hand history inside), `history` once per connection with the last
 * `LIMITS.handHistory` completed hands, `handRecord` with one new record each time a hand finishes, `serverError` on a rejected intent.
 */
export const SOCKET_EVENTS = { intent: 'intent', snapshot: 'snapshot', history: 'history', handRecord: 'handRecord', error: 'serverError' } as const;

export interface RoomConfig {
  smallBlind: number;
  bigBlind: number;
  defaultStack: number;
  actionTimerMs: number | null;   // per decision; null = no limit (nobody is auto-acted, except absent players)
  discardTimerMs: number;
  timeBankMs: number;             // extra time a player's clock rolls into automatically once it runs out; 0 = no bank
  timeBankHands: number;          // played hands after which the bank refills to full
  rabbitHunt: boolean;            // after a hand that ended before the river, a seated player may see the undealt board cards (privately)
  runItTwice: boolean;            // when the betting ends with an all-in and cards to come, ask the players; twice only if every one says yes
}

export const DEFAULT_CONFIG: RoomConfig = {
  smallBlind: 1,
  bigBlind: 2,
  defaultStack: 200,
  actionTimerMs: 20_000,   // PokerNow's defaults: 20 s to decide, a 10 s bank, refilled after 10 played hands
  discardTimerMs: 15_000,
  timeBankMs: 10_000,
  timeBankHands: 10,
  rabbitHunt: false,
  runItTwice: false,
};

export const LIMITS = {
  maxPlayers: 9,
  tokenMin: 16,
  tokenMax: 128,
  nicknameMax: 20,
  stackMultiple: 100,     // max requested stack or top-up = stackMultiple × defaultStack
  timerMinMs: 5_000,
  timerMaxMs: 300_000,
  timeBankMaxMs: 120_000,
  timeBankHandsMax: 100,
  chatMax: 500,
  chatHistory: 100,
  handHistory: 20,
  graceMs: 120_000,
  betweenHandsMs: 3_000,
  socketsPerRoom: 50,
  socketsPerToken: 5,
  maxRequests: 20,
  maxRooms: 1_000,
  roomsPerIp: 20,                 // rooms one address may create per window; the registry cap alone is a denial-of-service
  roomsPerIpWindowMs: 60 * 60_000,
} as const;

export type ClientIntent =
  | { type: 'sit'; seat: number; nickname: string; stack: number }
  | { type: 'topUp'; amount: number }
  | { type: 'approve'; requestId: string }
  | { type: 'deny'; requestId: string }
  | { type: 'standUp' }
  | { type: 'kick'; seat: number }
  | { type: 'transferHost'; seat: number }
  | { type: 'setConfig'; config: Partial<RoomConfig> }
  | { type: 'startGame' }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'shuffleSeats' }                 // host, between hands: the seated players swap seats at random; the button stays with its player
  | { type: 'endSession' }
  | { type: 'discard'; card: Card }
  | { type: 'fold' }
  | { type: 'check' }
  | { type: 'call' }
  | { type: 'raise'; to: number }
  | { type: 'showCards'; cards?: Card[] }   // after the hand: reveal both keepers (omitted) or one or two of them
  | { type: 'rabbitHunt' }                   // after a hand that ended before the river: see the undealt board cards (only on the asker's screen)
  | { type: 'runItTwice'; yes: boolean }     // the run-it-twice vote, while the hand waits for it
  | { type: 'setAway'; away: boolean }       // sit out from the next hand (true) or be dealt in again (false); a hand already under way is played out either way
  | { type: 'chat'; text: string };

export interface SeatRequest {
  id: string;
  kind: 'sit' | 'topUp';
  seat: number;
  nickname: string;
  amount: number;
}

export interface ChatMessage { id: number; t: number; nickname: string; text: string }

export interface LedgerRow { nickname: string; buyIns: number; current: number; net: number; seated: boolean }

export interface SeatView {
  seat: number;
  nickname: string;
  stack: number;
  connected: boolean;
  away: boolean;        // dealt out: sitting out by choice (setAway), or absent past the grace period
  busted: boolean;
  leaving: boolean;     // stand-up/kick pending until the hand ends
  isButton: boolean;
  inHand: boolean;
  folded: boolean;
  allIn: boolean;
  committedThisStreet: number;
  totalCommitted: number;
  discardLocked: boolean;
  discard: Card | null;       // own always; others only after the reveal
  keepers: Card[] | null;     // own only, once set (and the monitor)
  shown: Card[];              // publicly revealed keepers: every seat that reached showdown, plus voluntary shows; [] otherwise
  holeCards: Card[] | null;   // own only
  handName: string | null;    // seats that reached showdown with both keepers public
  handName2: string | null;   // the same seat's hand on the second board, when the board was run twice
  timeBankMs: number;         // extra decision time left in this player's bank
}

export interface HandView {
  handId: string;
  phase: Phase;
  buttonSeat: number;
  smallBlindSeat: number;
  bigBlindSeat: number;
  board: Card[];
  potTotal: number;
  pots: Array<{ amount: number; eligible: number[] }>;   // the collected chips, main pot first, each with the seats that can win it; this street's bets join when the street closes, or at once when the betting is over for good (the run-it-twice vote), less the top contributor's uncalled excess; [] once the hand is complete
  currentBet: number;
  actingSeat: number | null;
  actionDeadline: number | null;
  discardDeadline: number | null;
  extraTime: boolean;         // the action clock is running on the acting player's time bank
  commitment: string[];
  openings: CardOpening[];    // viewer's own hole openings + every public opening
  actions: HandRecordAction[]; // public action log so far (same records as the hand log)
  results: HandResults | null;
  rabbit: Card[] | null;       // the undealt board cards, for a viewer who asked to see them after an early finish (their openings join `openings`)
  rabbitHunt: boolean;         // whether rabbit hunting is allowed for this hand (the setting as it was at the deal; a change applies from the next hand)
  runItTwice: { deadline: number | null; asked: number[]; answered: number[] } | null;   // the vote in progress: who decides, who has answered (answers stay private), and when it closes
  board2: Card[] | null;       // the second board when the rest was run twice: the shared cards, then its own
  runOutFrom: number;          // how many board cards the two runs share
  upcoming?: Card[];           // monitor snapshots only: the board cards not yet dealt, in deal order
}

export type ActionStreet = 'discard' | 'preflop' | 'flop' | 'turn' | 'river';

export interface HandRecordAction {
  t: number;
  type: 'blind' | 'discardLocked' | 'fold' | 'check' | 'call' | 'raise' | 'show';
  seat: number;
  street: ActionStreet;   // the betting street the action belongs to (blinds: preflop; show: the hand's last street); absent in logs from before it existed
  amount?: number;
  to?: number;
  cards?: Card[];   // show: the cards revealed by this action
  auto?: boolean;
}

export interface HandRecord {
  handId: string;
  handNumber: number;
  startedAt: number;
  endedAt: number;
  smallBlind: number;
  bigBlind: number;
  buttonSeat: number;
  players: Array<{ seat: number; nickname: string; startStack: number }>;
  commitment: string[];
  actions: HandRecordAction[];
  discards: Array<{ seat: number; opening: CardOpening }>;
  board: CardOpening[];
  board2?: CardOpening[];      // run it twice: the second board's five openings (the shared ones repeated)
  showdown: Array<{ seat: number; keepers: Card[]; openings: CardOpening[]; categoryName: string }>;   // every seat that reached showdown, winners and losers
  showdown2?: Array<{ seat: number; keepers: Card[]; openings: CardOpening[]; categoryName: string }>;   // the same seats' hands on the second board
  shows: Array<{ seat: number; cards: Card[]; openings: CardOpening[] }>;   // voluntary reveals: folded players and fold-out winners (one or both keepers)
  refund: { seat: number; amount: number } | null;
  awards: Array<{ potIndex: number; amount: number; shares: Array<{ seat: number; amount: number }>; run?: 1 | 2 }>;   // `run`: this half of the pot, when run twice
  finalStacks: Array<{ seat: number; stack: number }>;
  mine?: { seat: number; holeCards: Card[]; discard: Card | null };   // the receiving player's own dealt cards in this hand: present only in the copy the server sends to the player who held that seat (the history and handRecord events), never in the public download; the replay shows their seat as they saw it
}

export interface TableSnapshot {
  roomId: string;
  serverTime: number;
  you: {
    seat: number | null;
    nickname: string | null;
    isHost: boolean;
    pendingRequest: { kind: 'sit' | 'topUp'; amount: number } | null;
  };
  config: RoomConfig;
  pendingConfig: RoomConfig | null;   // settings saved during a hand: they apply at the next deal, the current hand keeps `config`
  hostNickname: string | null;     // the host's nickname when seated, else null
  running: boolean;
  paused: boolean;
  ended: boolean;
  endRequested: boolean;   // the host pressed END during a hand: the table finishes this one and then ends
  seats: Array<SeatView | null>;   // length LIMITS.maxPlayers
  hand: HandView | null;
  legalActions: LegalActions | null;
  requests: SeatRequest[];         // host only, else []
  chat: ChatMessage[];
  ledger: LedgerRow[];
}

export interface ServerError { code: string; message: string }

/** One live room as the operator monitor lists it (`GET /<MONITOR_PATH>/rooms`). */
export interface MonitorRoomSummary {
  roomId: string; hostNickname: string | null; running: boolean; paused: boolean; ended: boolean;
  handNumber: number;                 // state.handCounter
  phase: Phase | null;                // live hand's phase, null between hands
  potTotal: number;                   // 0 between hands
  connections: number;                // open player sockets
  lastActivityAt: number;
  players: Array<{ seat: number; nickname: string; stack: number; connected: boolean; inHand: boolean }>;
}
